import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Button from "core/components/Button";
import Field from "core/components/Field";
import Flex from "core/components/Flex";
import {
  Panel,
  PanelContent,
  PanelHeader,
  PanelTitle,
} from "core/components/Panel";
import Tabs from "core/components/Tabs/Tabs";
import Typography from "core/components/Typography";
import {
  useAssignableOrganizationsListQuery,
  useUserByIdQuery,
} from "core/queries";
import {
  createMemberships,
  deleteMemberships,
  updateMemberships,
  updateUserProfileByUserId,
} from "modules/admin/actions";
import useAuthenticatedMutation from "core/hooks/useAuthenticatedMutation";
import {
  ROLES,
  TABLEAU_ROLES,
  ANALYTICS_HUB_USER_ROLES,
  SYSTEM_ROLE_KEYS,
} from "core/constants";
import useForm from "core/hooks/useForm";
import z, { success } from "zod";
import Navigate from "core/components/Navigate";
import { toast } from "sonner";
import Error from "core/components/Error";
import { passwordReset } from "modules/auth/actions";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/pro-regular-svg-icons";
import { MembershipCard, MembershipCardError } from "./MembershipCard";

const roleOptions = Object.entries(ROLES).map(([key, value]) => {
  return {
    label: value,
    value: key,
  };
});

const ahuRoleOptions = Object.entries(ANALYTICS_HUB_USER_ROLES).map(
  ([key, value]) => {
    return {
      label: value,
      value: key,
    };
  }
);

const biRoleOptions = Object.entries(TABLEAU_ROLES).map(([key, value]) => {
  return {
    label: value,
    value: key,
  };
});

const getAppRoleOptionsByOrgId = (orgId) => {
  if (orgId !== "populi") {
    return roleOptions.filter(
      (option) => !SYSTEM_ROLE_KEYS.includes(option.value)
    );
  }

  return roleOptions;
};

const getBiRoleOptionsByOrgId = (orgId) => {
  if (orgId !== "populi") {
    return biRoleOptions.filter((option) => option.value !== "creator");
  }

  return biRoleOptions;
};

const membershipsSchema = z
  .array(
    z.object({
      user_id: z.string(),
      organization_id: z.string(),
      organization_name: z.string(),
      role: z.enum(Object.keys(ROLES)),
      analytics_hub_access: z
        .enum(Object.keys(ANALYTICS_HUB_USER_ROLES))
        .default("none"),
      tableau_config: z.object({
        license: z.enum(Object.keys(TABLEAU_ROLES)),
        enabled: z.boolean(),
      }),
      direct_data_access_enabled: z.boolean().optional(),
      action: z.enum(["added", "removed", "updated", "none"]).default("none"),
      status: z.enum(["success", "error", "none"]).default("none"),
      errorMessage: z.string().nullish(),
    })
  )
  .default([]);

const prepareMembershipsForRequest = membershipsSchema.transform((data) => {
  return data.map((m) => {
    const { organization_name, action, status, errorMessage, ...keepFields } =
      m;
    return keepFields;
  });
});
const prepareMembershipsForDelete = membershipsSchema.transform((data) => {
  return data.map((m) => {
    return {
      user_id: m.user_id,
      organization_id: m.organization_id,
    };
  });
});

const profileSchema = z
  .object({
    name: z.string().min(2, { message: "Must be at least 2 characters long." }),
    alternateEmail: z.string().nullish(),
    enabled: z.boolean(),
  })
  .transform((data) => {
    // Prepare data for form submission
    const { alternateEmail, ...includedFields } = data;

    return {
      ...includedFields,
      alternate_email: alternateEmail || null,
    };
  });

const GeneralForm = ({ profile, isLoading }) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const { formValues, isDisabled, onFieldChange } = useForm({
    initialValues: profile,
    formSchema: profileSchema,
    shouldValidateWithSchema: true,
    disabledUntilTouched: true,
  });

  const updateMutationFn = useAuthenticatedMutation(updateUserProfileByUserId);
  const { mutate: saveUserProfile, isPending: isSaving } = useMutation({
    mutationFn: updateMutationFn,
    onError: (error, variables, onMutateResult, context) => {
      console.log("updateUserMutation.onError", { error });

      if (Array.isArray(error)) {
        setError(error[0]);
      } else if (typeof error === "string") {
        setError(error);
      } else {
        setError("There was an issue updating user.");
      }
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      console.log("updateUserMutation.onSuccess", {
        data,
        variables,
        onMutateResult,
        context,
      });

      if (data.code === 207) {
        setError(data.results[0]?.error ?? "There was an issue updating user.");
      } else {
        queryClient.invalidateQueries({
          queryKey: ["user-profiles", { userId: profile.user_id }],
        });
        toast.success("Updated user successfully!");
      }
    },
  });

  const handleSaveProfile = () => {
    // Use schema to get the transform
    const updatedUser = profileSchema.safeParse(formValues).data;
    console.log({ updatedUser });
    saveUserProfile({ userId: profile.userId, profile: updatedUser });
  };

  return (
    <>
      <Error>{error}</Error>
      <Flex direction="column" gap="0px">
        <Field
          type="text"
          id="name"
          labelText="Name"
          isRequired={true}
          showSkeleton={isLoading}
          value={formValues.name}
          onChange={(value, errorMessage) => {
            onFieldChange("name")(value, errorMessage);
          }}
        />
        <Field
          type="text"
          id="email"
          labelText="Email"
          isRequired={true}
          showSkeleton={isLoading}
          value={formValues.email}
          onChange={(value, errorMessage) => {
            onFieldChange("email")(value, errorMessage);
          }}
          isDisabled
        />
        <Field
          type="text"
          id="alternateEmail"
          labelText="Alternate Email"
          showSkeleton={isLoading}
          value={formValues.alternateEmail}
          onChange={(value, errorMessage) => {
            onFieldChange("alternateEmail")(value, errorMessage);
          }}
        />
        <Field
          id="enabled"
          type="radio"
          labelText="Status"
          hideError
          isDisabled={isSaving}
          value={formValues.enabled}
          onChange={(value, errorMessage) => {
            console.log({ value, errorMessage });
            onFieldChange("enabled")(value, errorMessage);
          }}
          //   isRequired={true}
          options={[
            { label: "Enabled", value: true },
            { label: "Disabled", value: false },
          ]}
        />
        <Flex direction="row" gap="16px" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleSaveProfile}
            disabled={isDisabled || isLoading}
            isLoading={isSaving}
          >
            Save Changes
          </Button>
        </Flex>
      </Flex>
    </>
  );
};

const MembershipsForm = ({
  userId,
  memberships,
  assignableOrgOptions = [],
}) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const [formValues, setFormValues] = useState(() => {
    console.log({
      memberships,
      parsed: membershipsSchema.safeParse(memberships),
    });
    return membershipsSchema.safeParse(memberships).data ?? [];
  });
  const existingMembershipIds = memberships.map((m) => m.organization_id);

  const onFieldChange =
    (membershipId, fieldName, action = "none") =>
    (value) => {
      setFormValues((previous) => {
        return previous.map((m) => {
          if (m.organization_id === membershipId) {
            return {
              ...m,
              [fieldName]: value,
              action,
            };
          }

          return m;
        });
      });
    };

  console.log();
  const onFieldChangeAfterResponse =
    (membershipId) =>
    (status, errorMessage = null, resetAction = false) => {
      setFormValues((previous) => {
        const membership = previous.find(
          (m) => m.organization_id === membershipId
        );

        console.log({ membershipId, membership, resetAction, status });

        if (resetAction && membership.action === "removed") {
          return previous.filter((m) => {
            return m.organization_id !== membershipId;
          });
        } else {
          return previous.map((m) => {
            if (m.organization_id === membershipId) {
              return {
                ...m,
                status,
                errorMessage,
                action: resetAction ? "none" : m.action,
              };
            }

            return m;
          });
        }
      });
    };
  const formMembershipChanges = membershipsSchema.safeParse(formValues);

  const handleAddNewMembershipRow = () => {
    setFormValues((previous) => {
      return previous.concat({
        user_id: userId,
        organization_id: "",
        organization_name: "",
        role: "orgUser",
        analytics_hub_access: "none",
        tableau_config: {
          license: "none",
          enabled: false,
        },
        direct_data_access_enabled: false,
        status: "none",
        errorMessage: null,
      });
    });
  };

  const handleRemoveMembership = (membershipId) => {
    setFormValues((previous) => {
      return previous.map((m) => {
        if (m.organization_id === membershipId) {
          return {
            ...m,
            action: "removed",
          };
        }

        return m;
      });
    });
  };

  const hasChanges =
    formMembershipChanges.success &&
    formMembershipChanges.data.some((m) =>
      ["added", "updated", "removed"].includes(m.action)
    );

  const createMembershipFn = useAuthenticatedMutation(createMemberships);
  const deleteMembershipFn = useAuthenticatedMutation(deleteMemberships);
  const updateMembershipFn = useAuthenticatedMutation(updateMemberships);

  const { mutate: saveMemberships, isPending: isSaving } = useMutation({
    mutationFn: async () => {
      const requests = {
        adds: null,
        removes: null,
        updates: null,
      };

      const formMembershipChanges =
        membershipsSchema.safeParse(formValues).data;
      const addedMemberships = formMembershipChanges.filter(
        (m) => m.action === "added"
      );
      const removedMemberships = formMembershipChanges.filter(
        (m) => m.action === "removed"
      );
      const updatedMemberships = formMembershipChanges.filter(
        (m) => m.action === "updated"
      );
      console.log({ addedMemberships, removedMemberships, updatedMemberships });

      if (addedMemberships.length) {
        const formattedAddedMemberships =
          prepareMembershipsForRequest.safeParse(addedMemberships).data;

        requests.adds = createMembershipFn({
          memberships: formattedAddedMemberships,
        });
      }

      if (updatedMemberships.length) {
        const formattedUpdatedMemberships =
          prepareMembershipsForRequest.safeParse(updatedMemberships).data;

        requests.updates = updateMembershipFn({
          memberships: formattedUpdatedMemberships,
        });
      }

      if (removedMemberships.length) {
        const formattedUpdatedMemberships =
          prepareMembershipsForDelete.safeParse(removedMemberships).data;

        requests.removes = deleteMembershipFn({
          memberships: formattedUpdatedMemberships,
        });
      }

      const [settledAdds, settledRemoves, settledUpdates] =
        await Promise.allSettled([
          requests.adds,
          requests.removes,
          requests.updates,
        ]);

      console.log({ settledAdds, settledRemoves, settledUpdates });

      const isResponseSuccess = (response) =>
        [200, 201].includes(response.code);
      const isResponseMixed = (response) => response.code === 207;
      const parseSettledState = ({ name, settledPromise, rejectedError }) => {
        const { status, value } = settledPromise;

        if (status === "rejected") {
          return [
            {
              status: "rejected",
              errorMessage: rejectedError,
            },
          ];
        }

        if (status === "fulfilled" && !value) {
          console.log(`[${name}] had no results`);
          return [];
        }

        if (status === "fulfilled") {
          const isSuccess = isResponseSuccess(value);
          const isMixed = isResponseMixed(value);

          if (isSuccess) {
            return value.results.map((result) => {
              return {
                status: "success",
                organization_id: result.organization_id,
              };
            });
          }

          if (isMixed) {
            return value.results.map((result) => {
              return {
                status: isResponseSuccess(result.status) ? "success" : "error",
                organization_id: result.organization_id,
                errorMessage: result.error,
              };
            });
          }
        }
      };

      const addMemberState = parseSettledState({
        name: "addMemberships",
        rejectedError: "There was an issue adding memberships.",
        settledPromise: settledAdds,
      });
      const removeMemberState = parseSettledState({
        name: "removeMemberships",
        rejectedError: "There was an issue removing memberships.",
        settledPromise: settledRemoves,
      });
      const updateMemberState = parseSettledState({
        name: "updateMemberships",
        rejectedError: "There was an issue updating memberships.",
        settledPromise: settledUpdates,
      });

      console.log({ addMemberState, removeMemberState, updateMemberState });

      return [...addMemberState, ...removeMemberState, ...updateMemberState];
    },
    onError: (error) => {
      if (Array.isArray(error)) {
        setError(error[0]);
      } else if (typeof error === "string") {
        setError(error);
      } else {
        setError("There was an issue updating memberships.");
      }
    },
    onSuccess: (data) => {
      console.log("saveMemberships.onSuccess", { data });

      const fulfilledResults = data.filter(
        (result) => result.status !== "rejected"
      );
      const fulfilledErrorResults = data.filter(
        (result) => result.status === "error"
      );
      const rejectedResults = data.filter(
        (result) => result.status === "rejected"
      );

      // Reset successful memberships changes in form back to action === "none"
      fulfilledResults.forEach((result) => {
        onFieldChangeAfterResponse(result.organization_id)(
          result.status,
          result.errorMessage,
          result.status === "success"
        );
      });

      if (rejectedResults.length > 0) {
        const combinedErrors = rejectedResults.map((r) => r.errorMessage);
        setError(combinedErrors.join("\n"));
      }

      if (rejectedResults.length === 0 && fulfilledErrorResults.length === 0) {
        toast.success("Updated user memberships successfully!");
        queryClient.refetchQueries({
          queryKey: ["user-profiles", { userId }],
        });
      }
    },
  });

  const handleSave = () => {
    setError(null);
    saveMemberships();
  };

  return (
    <Flex direction="column" gap="24px">
      <Error>{error}</Error>
      <Flex direction="column" gap="16px">
        <Flex direction="row" gap="16px">
          <Typography variant="body" noMargin>
            Users can only be members of organizations that allow their domain
            access.
          </Typography>
          <Button variant="text" onClick={handleAddNewMembershipRow}>
            Add
          </Button>
        </Flex>
      </Flex>

      {formValues
        .filter((m) => m.action !== "removed")
        .map((membership, index) => {
          console.log({ membership });
          const isExisting = existingMembershipIds.includes(
            membership.organization_id
          );
          const membershipId = membership.organization_id;
          const action = isExisting ? "updated" : "added";
          const hasOrgSelected = !!membership.organization_id;
          const takenOrgIds = formValues
            .map((m) => m.organization_id)
            .filter(Boolean);
          const filteredOrgOptions = assignableOrgOptions.filter((option) => {
            if (hasOrgSelected && option.value === membership.organization_id) {
              return true;
            }

            return !takenOrgIds.includes(option.value);
          });

          return (
            <MembershipCard key={membershipId} status={membership.status}>
              <Flex
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap="8px"
              >
                {isExisting ? (
                  <Typography variant="h5" noMargin>
                    {membership.organization_name}
                  </Typography>
                ) : (
                  <Field
                    type="combobox"
                    size="small"
                    id={`organization-${index}`}
                    placeholder="Select Organization"
                    isRequired
                    isSearchable
                    hideError
                    options={filteredOrgOptions}
                    value={membership.organization_id}
                    onChange={(value) => {
                      const { label: orgName, value: orgId } =
                        assignableOrgOptions.find((o) => o.value === value);

                      onFieldChange(
                        membershipId,
                        "organization_name",
                        action
                      )(orgName);
                      onFieldChange(
                        membershipId,
                        "organization_id",
                        action
                      )(orgId);
                    }}
                  />
                )}

                <Button
                  variant="icon"
                  size="small"
                  onClick={() => handleRemoveMembership(membershipId)}
                  style={{ marginRight: "4px" }}
                >
                  <FontAwesomeIcon icon={faClose} />
                </Button>
              </Flex>

              <Flex direction="row" gap="8px">
                <Field
                  type="combobox"
                  size="small"
                  id={`role-${index}`}
                  labelText="Role"
                  isRequired
                  hideError={true}
                  options={getAppRoleOptionsByOrgId(membershipId)}
                  value={membership.role}
                  onChange={onFieldChange(membershipId, "role", action)}
                />

                <Field
                  type="combobox"
                  id={`analyticsHubUser-${index}`}
                  size="small"
                  labelText="Analytics Hub User"
                  isRequired
                  hideError={true}
                  options={ahuRoleOptions}
                  value={membership.analytics_hub_access}
                  onChange={onFieldChange(
                    membershipId,
                    "analytics_hub_access",
                    action
                  )}
                />
              </Flex>

              <Flex direction="row" gap="8px">
                <Field
                  type="combobox"
                  id={`biPortalUser-${index}`}
                  size="small"
                  labelText="BI Portal User"
                  isRequired
                  hideError={true}
                  options={getBiRoleOptionsByOrgId(membershipId)}
                  value={membership.tableau_config?.license ?? "none"}
                  onChange={(value) => {
                    onFieldChange(
                      membershipId,
                      "tableau_config",
                      action
                    )({
                      license: value,
                      enabled: value !== "none",
                    });
                  }}
                />

                <Field
                  type="combobox"
                  id={`directDataAccess-${index}`}
                  size="small"
                  labelText="Data Access"
                  isRequired
                  hideError={true}
                  options={[
                    { label: "Enabled", value: true },
                    { label: "Disabled", value: false },
                  ]}
                  value={membership.direct_data_access_enabled}
                  onChange={onFieldChange(
                    membershipId,
                    "direct_data_access_enabled",
                    action
                  )}
                />
              </Flex>
              {membership.errorMessage && (
                <MembershipCardError>
                  {membership.errorMessage}
                </MembershipCardError>
              )}
            </MembershipCard>
          );
        })}

      <Flex direction="row" gap="16px" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges}
          isLoading={isSaving}
        >
          Save Changes
        </Button>
      </Flex>
    </Flex>
  );
};

const PasswordForm = ({ profile }) => {
  const [isLoading, setIsLoading] = useState(false);

  return (
    <Flex direction="column" gap="16px">
      <Flex key="12" direction="column" gap="16px">
        <Flex direction="column" gap="16px">
          <Flex direction="row" gap="4px">
            <Typography variant="body" noMargin>
              Instructions to reset password will be sent to the email
              associated with this user.
            </Typography>
          </Flex>
        </Flex>
        <Flex direction="row" gap="16px" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={() => {
              setIsLoading(true);
              passwordReset({ email: profile.email })
                .then(() => {
                  toast.success("Reset password email has been sent.");
                  setIsLoading(false);
                })
                .catch((error) => {
                  console.error(error);
                  setIsLoading(false);
                });
            }}
            isLoading={isLoading}
          >
            Reset Password
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

const EditUserPanel = () => {
  const [isOpen, setOpen] = useState(true);
  const { userId } = useParams();

  const {
    data: user,
    isLoading: isLoadingProfile,
    isPending: isPendingProfile,
    isFetching: isFetchingProfile,
  } = useUserByIdQuery({
    userId,
    // Matt: Providing placeholder data makes isLoading (and isPending) false (never true)
    // Matt: placeholderData must match shape of select return
    placeholderData: {
      profile: {},
      memberships: [],
    },
    select: (data) => {
      return {
        profile: {
          userId: data.user_id,
          name: data.name,
          email: data.email,
          alternateEmail: data.alternate_email,
          enabled: data.enabled,
          accessDetails: data.access_details,
        },
        memberships: data.memberships.map((m) => {
          m.user_id = userId;
          return m;
        }),
      };
    },
  });
  const {
    data: assignableOrgOptions = [],
    isFetching: isFetchingAssignableOrgs,
  } = useAssignableOrganizationsListQuery({
    email: user.profile.email,
    enabled: !!user.profile.email,
    select: (data) =>
      data.map((assignableOrg) => ({
        label: assignableOrg.organization_name,
        value: assignableOrg.organization_id,
      })),
  });

  const isLoading = [isFetchingProfile, isFetchingAssignableOrgs].includes(
    true
  );

  if (!isLoading && !user) {
    return <Navigate to="../" />;
  }

  return (
    <Panel isOpen={isOpen} onOpenChange={setOpen} onCloseRedirectTo="../">
      <PanelHeader>
        <PanelTitle>Edit User</PanelTitle>
      </PanelHeader>
      <PanelContent>
        <Tabs
          type="content"
          basis="full"
          items={[
            {
              id: "general",
              title: "General",
              content: (
                <GeneralForm profile={user.profile} isLoading={isLoading} />
              ),
            },
            {
              id: "memberships",
              title: "Memberships",
              disabled: isLoading,
              content: (
                <MembershipsForm
                  userId={userId}
                  memberships={user.memberships}
                  assignableOrgOptions={assignableOrgOptions}
                />
              ),
            },
            {
              id: "password",
              title: "Password",
              disabled: isLoading,
              content: <PasswordForm profile={user.profile} />,
            },
          ]}
        />
      </PanelContent>
    </Panel>
  );
};

export default EditUserPanel;
